import { NotificationData } from 'src/utils/notifications';
import { partition } from 'lodash';
import { useCallback, useMemo } from 'react';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import getHasSeenAlertNotificationKey from 'src/components/Toasts/AlertNotifications/utils/getHasSeenAlertNotificationKey';
import MobileAlertNotifications from 'src/components/Toasts/AlertNotifications/MobileAlertNotifications';
import MinorAlertNotifications from 'src/components/Toasts/AlertNotifications/MinorAlertNotifications/MinorAlertNotifications';
import MajorAlertNotification from 'src/components/Toasts/AlertNotifications/MajorAlertNotifications';

interface Props {
	notifications: NotificationData[];
	footerHeight: number;
}

export default function AlertNotifications({
	notifications,
	footerHeight,
}: Props) {
	const isMobile = useIsMobileScreenSize();

	const storeHasSeenAlertNotification = useCallback((id: string) => {
		localStorage.setItem(getHasSeenAlertNotificationKey(id), 'true');
	}, []);

	const sortedNotifications = useMemo(() => {
		return [...notifications].sort((a, b) => {
			if (a.stackIndex === undefined && b.stackIndex === undefined) {
				return 0;
			}
			if (a.stackIndex === undefined) {
				return 1;
			}
			if (b.stackIndex === undefined) {
				return -1;
			}
			return a.stackIndex - b.stackIndex;
		});
	}, [notifications]);

	const [majorAlertNotifications, minorAlertNotifications] = useMemo(
		() => partition(sortedNotifications, (n) => n.alertType === 'major'),
		[sortedNotifications]
	);

	if (sortedNotifications.length === 0) {
		return null;
	}

	if (isMobile) {
		return (
			<MobileAlertNotifications
				notifications={sortedNotifications}
				onDismiss={storeHasSeenAlertNotification}
			/>
		);
	}

	return (
		<>
			{majorAlertNotifications.length > 0 && (
				<MajorAlertNotification
					// Should only ever have one major alert notification.
					notification={majorAlertNotifications[0]}
					footerHeight={footerHeight}
					onDismiss={storeHasSeenAlertNotification}
				/>
			)}
			{minorAlertNotifications.length > 0 && (
				<MinorAlertNotifications
					notifications={minorAlertNotifications}
					onDismiss={storeHasSeenAlertNotification}
				/>
			)}
		</>
	);
}
